import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, CheckCircle, XCircle, Clock, ArrowLeft, MessageCircle, RefreshCw } from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { track } from "@/lib/analytics";
import { supabase } from "@/lib/firebase";
import { WHATSAPP_NUMBER } from "@/lib/cart";
import { useCartContext } from "@/contexts/CartContext";

const WA_PAYMENT_FAIL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi, I tried paying via PhonePe twice and it failed. Can you help me place my order manually?")}`;
const RETRY_KEY = "pp_retries";

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 8; // ~24 seconds of polling

export function PaymentStatusPage() {
  useSEO({ title: "Payment Status", description: "Check your PhonePe payment status." });

  const [params] = useSearchParams();
  const navigate = useNavigate();
  // The orderId URL param is actually a pending_checkouts id until payment
  // is confirmed — the real order doesn't exist yet (see resulting_order_id).
  const checkoutId = params.get("orderId");
  const { clear } = useCartContext();

  const [phase, setPhase] = useState("loading"); // "loading" | "success" | "pending" | "failed" | "error"
  const [orderNum, setOrderNum] = useState(null);
  const [resultingOrderId, setResultingOrderId] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [recheckTrigger, setRecheckTrigger] = useState(0);
  const retryCount = parseInt(sessionStorage.getItem(RETRY_KEY) || "0", 10);

  const handleRetry = () => {
    sessionStorage.setItem(RETRY_KEY, String(retryCount + 1));
    track("phonepe_payment_retry", { checkout_id: checkoutId, retry_count: retryCount + 1 });
    navigate("/checkout");
  };

  const handleCheckAgain = () => {
    setPollCount(0);
    setPhase("loading");
    setRecheckTrigger((n) => n + 1);
  };

  useEffect(() => {
    if (!checkoutId) {
      navigate("/", { replace: true });
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    async function checkPendingCheckout() {
      try {
        const { data: pending, error } = await supabase
          .from("pending_checkouts")
          .select("id, status, resulting_order_id")
          .eq("id", checkoutId)
          .maybeSingle();

        if (cancelled) return;

        if (error || !pending) {
          setPhase("error");
          return;
        }

        if (pending.status === "completed") {
          const newOrderId = pending.resulting_order_id;
          setResultingOrderId(newOrderId);

          const { data: order } = await supabase
            .from("orders")
            .select("order_number")
            .eq("id", newOrderId)
            .maybeSingle();
          setOrderNum(order?.order_number || newOrderId.slice(0, 8).toUpperCase());

          sessionStorage.removeItem(RETRY_KEY);
          clear(); // payment confirmed — safe to empty the cart now
          setPhase("success");
          track("phonepe_payment_success", { checkout_id: checkoutId, order_id: newOrderId, order_number: order?.order_number });
          // Redirect to order page after a short delay
          timer = setTimeout(() => {
            if (!cancelled) navigate(`/order/${newOrderId}`, { replace: true });
          }, 2500);
          return;
        }

        if (pending.status === "failed") {
          setPhase("failed");
          track("phonepe_payment_failed", { checkout_id: checkoutId });
          return;
        }

        // still pending — payment not yet confirmed by webhook/reconcile
        setPollCount(c => {
          const next = c + 1;
          if (next >= MAX_POLLS) {
            setPhase("pending");
            return next;
          }
          timer = setTimeout(checkPendingCheckout, POLL_INTERVAL_MS);
          return next;
        });
      } catch (err) {
        if (!cancelled) {
          console.error("PaymentStatusPage:", err);
          setPhase("error");
        }
      }
    }

    checkPendingCheckout();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [checkoutId, navigate, clear, recheckTrigger]);

  const orderLabel = orderNum
    ? `#${orderNum}`
    : resultingOrderId
      ? `#${resultingOrderId.slice(0, 8).toUpperCase()}`
      : "";

  return (
    <div className="min-h-screen bg-[#F5E6CC] grid place-items-center px-4 py-12">
      <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-10 max-w-sm w-full text-center">

        {phase === "loading" && (
          <>
            <Loader2 className="w-14 h-14 animate-spin text-[#5A2D1D] mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold text-[#5A2D1D] mb-2">Checking Payment…</h2>
            <p className="text-sm text-[#2E1A12]/70">Please wait while we confirm your payment with PhonePe.</p>
          </>
        )}

        {phase === "success" && (
          <>
            <CheckCircle className="w-16 h-16 text-[#556B2F] mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold text-[#5A2D1D] mb-2">Payment Successful!</h2>
            <p className="text-sm text-[#2E1A12]/70 mb-1">Order {orderLabel} confirmed.</p>
            <p className="text-xs text-[#2E1A12]/50 mb-6">Redirecting to your order…</p>
            <Link
              to={`/order/${resultingOrderId}`}
              className="inline-block bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-6 py-3 rounded-full font-bold text-sm transition-colors"
            >
              View Order
            </Link>
          </>
        )}

        {phase === "pending" && (
          <>
            <Clock className="w-14 h-14 text-[#BF8C30] mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold text-[#5A2D1D] mb-2">Payment Processing</h2>
            <p className="text-sm text-[#2E1A12]/70 mb-2">
              We're still confirming your payment with PhonePe — this can take a few minutes.
            </p>
            <p className="text-xs text-[#2E1A12]/50 mb-6">
              If the amount was debited, your order will be created automatically as soon as it's confirmed. No need to pay again.
            </p>
            <button
              onClick={handleCheckAgain}
              className="inline-flex items-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-6 py-3 rounded-full font-bold text-sm transition-colors"
            >
              <RefreshCw className="w-4 h-4" /> Check Again
            </button>
          </>
        )}

        {phase === "failed" && (
          <>
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold text-[#5A2D1D] mb-2">Payment Failed</h2>
            <p className="text-sm text-[#2E1A12]/70 mb-6">
              {retryCount >= 2
                ? "Your payment failed again. Please contact us on WhatsApp and we'll help you place the order."
                : "The payment could not be completed. Your order was not placed. Please try again."}
            </p>
            {retryCount >= 2 ? (
              <>
                <a
                  href={WA_PAYMENT_FAIL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("whatsapp_contact_clicked", { source: "payment_failed", retry_count: retryCount })}
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebc5a] text-white px-6 py-3 rounded-full font-bold text-sm transition-colors mb-3"
                >
                  <MessageCircle className="w-4 h-4" /> Contact on WhatsApp
                </a>
                <br />
                <Link to="/products" className="text-sm text-[#556B2F] hover:underline">
                  Continue Shopping
                </Link>
              </>
            ) : (
              <>
                <button
                  onClick={handleRetry}
                  className="inline-flex items-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-6 py-3 rounded-full font-bold text-sm transition-colors mb-3"
                >
                  <RefreshCw className="w-4 h-4" /> Retry Payment
                </button>
                <br />
                <Link to="/products" className="text-sm text-[#556B2F] hover:underline">
                  Continue Shopping
                </Link>
              </>
            )}
          </>
        )}

        {phase === "error" && (
          <>
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold text-[#5A2D1D] mb-2">Something Went Wrong</h2>
            <p className="text-sm text-[#2E1A12]/70 mb-6">
              We couldn't retrieve your order status. If money was debited, please WhatsApp us and we'll confirm your order manually.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#5A2D1D] font-semibold hover:underline">
              <ArrowLeft className="w-4 h-4" /> Go Home
            </Link>
          </>
        )}

      </div>
    </div>
  );
}
